// src/components/LanguageSwitcher/LanguageSwitcher.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { LanguageCode } from '../../config/i18n';
import {
  isMarketingPath,
  localizePath,
  marketingLocaleFromLanguage,
  normalizeMarketingPathname,
} from '../../config/localeRoutes';
import './LanguageSwitcher.css';

interface LanguageSwitcherProps {
    variant?: 'dropdown' | 'inline' | 'select';
    showLabel?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
    variant = 'dropdown',
    showLabel = false
}) => {
    const { currentLanguage, changeLanguage, availableLanguages, isLoading } = useLanguage();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);

    const handleLanguageChange = async (langCode: string) => {
        const lang = langCode as LanguageCode;

        if (isMarketingPath(location.pathname)) {
            const targetLocale = marketingLocaleFromLanguage(lang);
            const newPath = localizePath(location.pathname, targetLocale);
            if (
                normalizeMarketingPathname(newPath) !==
                normalizeMarketingPathname(location.pathname)
            ) {
                navigate(newPath);
                setIsOpen(false);
                return;
            }
        }

        await changeLanguage(lang);
        setIsOpen(false);
    };

    const currentLang = availableLanguages.find(lang => lang.code === currentLanguage);

    // Select variant using MUI Select
    if (variant === 'select') {
        return (
            <div className="language-switcher-select">
                <FormControl fullWidth size="medium" sx={{ maxWidth: 300 }}>
                    <Select
                        value={currentLanguage}
                        onChange={(e) => void handleLanguageChange(String(e.target.value))}
                        disabled={isLoading}
                    >
                        {availableLanguages.map(lang => (
                            <MenuItem key={lang.code} value={lang.code}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '1.2em' }}>{lang.flag}</span>
                                    <span>{lang.name}</span>
                                </div>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
        );
    }

    if (variant === 'inline') {
        return (
            <div className="language-switcher-inline">
                {availableLanguages.map((lang) => (
                    <button
                        key={lang.code}
                        className={`lang-button ${currentLanguage === lang.code ? 'active' : ''}`}
                        onClick={() => handleLanguageChange(lang.code)}
                        disabled={isLoading}
                    >
                        <span className="lang-flag">{lang.flag}</span>
                        <span className="lang-name">{lang.name}</span>
                    </button>
                ))}
            </div>
        );
    }

    return (
        <div className="language-switcher-dropdown">
            <button
                className="lang-trigger"
                onClick={() => setIsOpen(!isOpen)}
                disabled={isLoading}
                aria-label={t('language')}
            >
                <span className="lang-flag">{currentLang?.flag}</span>
                {showLabel && <span className="lang-label">{currentLang?.name}</span>}
                <svg
                    className={`chevron ${isOpen ? 'open' : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M2.5 4.5L6 8L9.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {isOpen && (
                <>
                    <div className="lang-backdrop" onClick={() => setIsOpen(false)} />
                    <div className="lang-menu">
                        {availableLanguages.map((lang) => (
                            <button
                                key={lang.code}
                                className={`lang-option ${currentLanguage === lang.code ? 'active' : ''}`}
                                onClick={() => handleLanguageChange(lang.code)}
                            >
                                <span className="lang-flag">{lang.flag}</span>
                                <span className="lang-name">{lang.name}</span>
                                {currentLanguage === lang.code && (
                                    <svg
                                        className="checkmark"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M13.5 4.5L6 12L2.5 8.5"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
